import React, { useEffect, useState } from 'react';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import spformsApi from '../lib/spformsApi';
import { useAuth } from '../contexts/AuthContext';
import { useTenant } from '../contexts/TenantContext';

const {
  FiShield,
  FiSave,
  FiRefreshCw,
  FiCheckCircle,
  FiAlertCircle,
  FiShieldOff,
} = FiIcons;

const RECAPTCHA_KEYS = [
  {
    key: 'recaptcha_enterprise_enabled',
    label: 'Enable reCAPTCHA Enterprise',
    type: 'boolean',
    public: true,
  },
  {
    key: 'recaptcha_enterprise_site_key',
    label: 'Site Key',
    type: 'text',
    public: true,
  },
  {
    key: 'recaptcha_enterprise_project_id',
    label: 'Project ID',
    type: 'text',
    public: false,
  },
  {
    key: 'recaptcha_enterprise_action_login',
    label: 'Login Action',
    type: 'text',
    default: 'login',
    public: true,
  },
  {
    key: 'recaptcha_enterprise_action_registration',
    label: 'Registration Action',
    type: 'text',
    default: 'registration_submit',
    public: true,
  },
];

export default function SuperSettings() {
  const { session } = useAuth();
  const { isSuperadmin } = useTenant();

  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const loadSettings = async () => {
    if (!isSuperadmin || !session?.token) return;

    try {
      setLoading(true);
      setErrorMessage('');
      setSuccessMessage('');

      const data = await spformsApi.settingsGetAll(session.token);

      const settingsMap = {};
      data.forEach((s) => {
        settingsMap[s.key] = s.value;
      });

      RECAPTCHA_KEYS.forEach((item) => {
        if (settingsMap[item.key] === undefined) {
          if (item.type === 'boolean') {
            settingsMap[item.key] = 'false';
          } else if (item.default !== undefined) {
            settingsMap[item.key] = item.default;
          } else {
            settingsMap[item.key] = '';
          }
        }
      });

      setSettings(settingsMap);
    } catch (error) {
      setErrorMessage(error.message || 'Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSettings();
  }, [isSuperadmin, session?.token]);

  const handleChange = (key, value) => {
    setSettings((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleSave = async (event) => {
    event.preventDefault();

    if (!isSuperadmin || !session?.token) return;

    try {
      setSaving(true);
      setErrorMessage('');
      setSuccessMessage('');

      for (const config of RECAPTCHA_KEYS) {
        const value =
          settings[config.key] ??
          (config.type === 'boolean' ? 'false' : config.default || '');

        await spformsApi.settingsUpsert(
          session.token,
          config.key,
          String(value),
          config.public
        );
      }

      setSuccessMessage('Global security settings saved successfully.');
      await loadSettings();
    } catch (error) {
      setErrorMessage(error.message || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (!isSuperadmin) {
    return (
      <div className="rounded-2xl border border-red-100 bg-red-50 p-8 text-center">
        <div className="mb-4 flex justify-center text-red-500">
          <SafeIcon icon={FiShieldOff} size={48} />
        </div>
        <h2 className="text-xl font-black text-red-900">Access Denied</h2>
        <p className="mt-2 text-sm font-bold text-red-700">
          Only superadmins can manage global platform settings.
        </p>
      </div>
    );
  }

  return (
    <div className="max-w-4xl space-y-8">
      <div>
        <h1 className="text-4xl font-black tracking-tighter text-gray-900">
          Global Settings
        </h1>
        <p className="font-medium tracking-tight text-gray-500">
          Manage platform-wide security, integrations, and global configurations.
        </p>
      </div>

      {errorMessage && (
        <div className="flex items-center gap-3 rounded-2xl border border-red-100 bg-red-50 px-5 py-4 text-sm font-bold text-red-700">
          <SafeIcon icon={FiAlertCircle} />
          {errorMessage}
        </div>
      )}

      {successMessage && (
        <div className="flex items-center gap-3 rounded-2xl border border-emerald-100 bg-emerald-50 px-5 py-4 text-sm font-bold text-emerald-700">
          <SafeIcon icon={FiCheckCircle} />
          {successMessage}
        </div>
      )}

      <div className="overflow-hidden rounded-[2.5rem] border bg-white shadow-sm">
        <div className="flex items-center gap-4 border-b bg-gray-50/50 p-8">
          <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-indigo-600 text-white">
            <SafeIcon icon={FiShield} size={24} />
          </div>
          <div>
            <h2 className="text-xl font-black tracking-tight text-gray-900">
              reCAPTCHA Enterprise
            </h2>
            <p className="mt-1 text-xs font-bold uppercase tracking-widest text-gray-400">
              Global bot protection for login and public registration
            </p>
          </div>
        </div>

        {loading ? (
          <div className="p-20 text-center text-xs font-black uppercase tracking-[0.4em] text-gray-400 animate-pulse">
            Loading settings...
          </div>
        ) : (
          <form onSubmit={handleSave} className="space-y-8 p-10">
            <div className="space-y-6">
              {RECAPTCHA_KEYS.map((config) => (
                <div key={config.key} className="space-y-2">
                  {config.type === 'boolean' ? (
                    <div className="flex items-center gap-4 rounded-2xl border-2 border-gray-100 bg-gray-50/50 p-6">
                      <input
                        id={config.key}
                        type="checkbox"
                        checked={settings[config.key] === 'true'}
                        onChange={(e) =>
                          handleChange(
                            config.key,
                            e.target.checked ? 'true' : 'false'
                          )
                        }
                        className="h-6 w-6 rounded border-2 border-gray-300 text-indigo-600 focus:ring-indigo-500"
                      />
                      <label
                        htmlFor={config.key}
                        className="cursor-pointer text-sm font-black uppercase tracking-widest text-gray-700"
                      >
                        {config.label}
                      </label>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <label className="ml-2 text-[10px] font-black uppercase tracking-[0.2em] text-gray-400">
                        {config.label}
                      </label>
                      <input
                        type="text"
                        value={settings[config.key] || ''}
                        onChange={(e) =>
                          handleChange(config.key, e.target.value)
                        }
                        placeholder={config.default || ''}
                        className="w-full rounded-2xl border-2 border-gray-100 bg-white px-6 py-4 font-bold text-gray-900 outline-none transition-all focus:border-indigo-600"
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div className="rounded-2xl border border-amber-100 bg-amber-50 p-5 text-xs font-bold leading-relaxed text-amber-800">
              <div className="mb-2 flex items-center gap-3 text-amber-600">
                <SafeIcon icon={FiAlertCircle} />
                <span className="font-black uppercase tracking-widest">
                  Validation Notice
                </span>
              </div>
              reCAPTCHA remains inactive until enabled and fully configured with
              a valid Site Key and action names. Project ID is stored privately
              for future backend verification.
            </div>

            <div className="flex justify-end gap-4 border-t pt-8">
              <button
                type="button"
                onClick={loadSettings}
                disabled={saving}
                className="flex items-center gap-2 px-8 py-4 text-xs font-black uppercase tracking-widest text-gray-400 transition-colors hover:text-gray-600 disabled:opacity-60"
              >
                <SafeIcon icon={FiRefreshCw} />
                Reset
              </button>

              <button
                type="submit"
                disabled={saving}
                className="flex items-center gap-3 rounded-[1.5rem] bg-indigo-600 px-12 py-4 text-xs font-black uppercase tracking-[0.2em] text-white shadow-2xl shadow-indigo-100 transition-all hover:bg-indigo-700 disabled:opacity-60"
              >
                {saving ? (
                  <SafeIcon icon={FiRefreshCw} className="animate-spin" />
                ) : (
                  <SafeIcon icon={FiSave} />
                )}
                Save Security Settings
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}